/**
 * useZonePalette — tiny shared hook so every location grid and SKU
 * card renders the same colour for the same zone code.
 *
 * Falls back to a deterministic hash-based colour when the tenant has
 * not yet configured that zone, so operators still see distinguishable
 * chips instead of a bland grey wall.
 */
import { useEffect, useState, useCallback } from 'react';
import { listZonePalette } from './api';

const FALLBACK_COLOURS = [
  '#0EA5E9', '#1D4ED8', '#F97316', '#84CC16', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F43F5E', '#78716C',
];

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function contrastText(hex) {
  // WCAG-ish contrast pick — white on dark, near-black on light.
  const c = (hex || '#888888').replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 155 ? '#1F2937' : '#FFFFFF';
}

let cache = null;

export function useZonePalette() {
  const [map, setMap] = useState(cache);
  const refresh = useCallback(async () => {
    try {
      const r = await listZonePalette();
      const m = {};
      (r.data || []).forEach((e) => {
        m[String(e.zone_code || '').toUpperCase()] = e;
      });
      cache = m;
      setMap(m);
    } catch { /* silent — falls back to hash colours */ }
  }, []);
  useEffect(() => { if (!cache) refresh(); else setMap(cache); }, [refresh]);

  const style = (zoneCode) => {
    const code = String(zoneCode || '').toUpperCase();
    const hit = map?.[code];
    const bg = hit?.color || FALLBACK_COLOURS[hashCode(code) % FALLBACK_COLOURS.length];
    return {
      code,
      label: hit?.label || code,
      icon: hit?.icon || '',
      bg,
      fg: contrastText(bg),
      configured: !!hit,
    };
  };

  return { map, style, refresh };
}
